<template>
  <div v-if="displaySelect" class="autocomplete" data-testid="autocomplete">
    <select
      class="autocomplete__select"
      multiple
      :size="emailDomainArray.length"
      data-testid="autocomplete-select"
      @keypress.enter="updateEmail(emailDomainArray[0])"
    >
      <option
        v-for="(value, key) in emailDomainArray"
        :key="key"
        class="autocomplete__option"
        :value="value"
        tabindex="0"
        data-testid="autocomplete-option"
        @click="updateEmail(value)"
        @keypress.enter.stop="updateEmail(value)"
      >
        {{ value }}
      </option>
    </select>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { knownServices } from '@/modules/onboarding/signup/helpers/email-suggester';

const emailDomainArray = ref<string[]>([]);
const closedByClick = ref<boolean>(false);

const props = defineProps({
  email: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update-email']);

function updateEmail(value: string) {
  const email = props.email.split('@');
  emit('update-email', `${email[0]}${value}`);
}

const displaySelect = computed(() => {
  const emailDomain = emailDomainArray.value.find(domain =>
    props.email.includes(domain)
  );
  return (
    props.email.includes('@') &&
    !props.email.includes('.com') &&
    !emailDomain &&
    !closedByClick.value &&
    emailDomainArray.value.length > 0
  );
});

function toggleClose() {
  closedByClick.value = !closedByClick.value;
}

watch(
  () => displaySelect.value,
  newValue => {
    if (newValue) {
      document.body.addEventListener('click', toggleClose);
    } else {
      document.body.removeEventListener('click', toggleClose);
    }
  }
);
watch(
  () => props.email,
  () => {
    closedByClick.value = false;
    emailDomainArray.value = knownServices
      .filter(entry =>
        entry.domain.includes(props.email.toLowerCase().split('@')[1])
      )
      .map(entry => `@${entry.domain}`);
    if (emailDomainArray.value.includes('')) {
      emailDomainArray.value = knownServices.map(entry => `@${entry.domain}`);
    }
  }
);
onMounted(() => {
  knownServices.forEach(entry => {
    emailDomainArray.value.push(`@${entry.domain}`);
  });
});
</script>
<style lang="less" scoped>
.autocomplete {
  position: relative;
  width: 100%;
  z-index: 5000;
  .autocomplete__select {
    width: 100%;
    max-height: 186px;
    position: absolute;
    margin-top: @size-spacing-x150;
    background-color: @element-negative;
    border: @size-border-x400 solid @divider-primary;
    border-radius: @size-radius-x300;
    &::-webkit-scrollbar {
      display: none;
    }
    .autocomplete__option {
      transition: background 0.2s ease-in;
      padding: @size-spacing-x400 @size-spacing-x350;
      &:hover,
      &::selection {
        background: @background-hover;
      }
    }
  }
}
</style>
